import { Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FedexPrefillData, IntlAddress, ShippingChoice } from '../../models/fedex-shipping.model';

@Component({
    selector: 'app-fedex-shipping-modal',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './fedex-shipping-modal.component.html',
    styleUrl: './fedex-shipping-modal.component.scss',
})
export class FedexShippingModalComponent {
    visible = input<boolean>(false);
    prefillData = input<FedexPrefillData | null>(null);
    loading = input<boolean>(false);
    error = input<string | null>(null);

    close = output<void>();
    shippingChoice = output<ShippingChoice>();

    // UI state
    shipType = signal<'own' | 'normal_mail' | 'fedex' | null>(null);
    openTicketsOptions = signal(false);
    openShippingFromOptions = signal(false);
    openShippingToOptions = signal(false);
    showSendIntlAddress = signal(false);
    showReceiveIntlAddress = signal(false);
    shippingAlert = signal<string | null>(null);

    // FedEx type selections
    fedexTypeSend = signal<string | null>(null);
    fedexTypeReceive = signal<string | null>(null);

    // International address forms
    sendIntlAddress: IntlAddress = this.emptyIntlAddress();
    receiveIntlAddress: IntlAddress = this.emptyIntlAddress();

    // Edit address modal
    showEditAddressModal = signal(false);
    editAddressTarget = signal<'send' | 'return'>('send');

    // Editable address (pre-filled from PDF form data)
    sendAddress = signal({
        fullName: '', phone: '', address: '', city: '', state: '', zipcode: '', fullAddress: '', email: ''
    });
    returnAddress = signal({
        fullName: '', phone: '', address: '', city: '', state: '', zipcode: '', fullAddress: '', email: ''
    });

    // Temp address for the edit modal
    tempAddress = {
        fullName: '', phone: '', address: '', city: '', state: '', zipcode: '', fullAddress: '', email: ''
    };

    // Computed display address
    sendFullAddress = computed(() => {
        const a = this.sendAddress();
        return a.address ? `${a.fullName}, ${a.address}, ${a.city}, ${a.state} ${a.zipcode}` : '';
    });

    returnFullAddress = computed(() => {
        const a = this.returnAddress();
        return a.address ? `${a.fullName}, ${a.address}, ${a.city}, ${a.state} ${a.zipcode}` : '';
    });

    private hasInitialized = false;

    constructor() {
        // We'll auto-fill when visible changes to true via ngOnChanges-like check
    }

    ngOnChanges() {
        if (this.visible() && !this.hasInitialized) {
            this.initFromPrefill();
            this.hasInitialized = true;
        }
        if (!this.visible()) {
            this.hasInitialized = false;
        }
    }

    private initFromPrefill() {
        const data = this.prefillData();
        if (!data) return;

        const fullName = data.fullName || `${data.firstName} ${data.lastName}`.trim();
        const addr = {
            fullName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            email: data.email || '',
            fullAddress: `${fullName}, ${data.address}, ${data.city}, ${data.state} ${data.zipcode}`
        };

        this.sendAddress.set({ ...addr });
        this.returnAddress.set({ ...addr });
    }

    selectShipType(type: 'own' | 'normal_mail' | 'fedex') {
        this.shipType.set(type);
        this.openTicketsOptions.set(type === 'fedex');

        if (type !== 'fedex') {
            this.openShippingFromOptions.set(false);
            this.openShippingToOptions.set(false);
            this.fedexTypeSend.set(null);
            this.fedexTypeReceive.set(null);
        }
    }

    toggleSendOptions() {
        this.openShippingFromOptions.update(v => !v);
        if (!this.openShippingFromOptions()) {
            this.fedexTypeSend.set(null);
            this.showSendIntlAddress.set(false);
        }
    }

    toggleReceiveOptions() {
        this.openShippingToOptions.update(v => !v);
        if (!this.openShippingToOptions()) {
            this.fedexTypeReceive.set(null);
            this.showReceiveIntlAddress.set(false);
        }
    }

    selectSendType(value: string) {
        this.fedexTypeSend.set(value);
        this.showSendIntlAddress.set(value === 'intl');
    }

    selectReceiveType(value: string) {
        this.fedexTypeReceive.set(value);
        this.showReceiveIntlAddress.set(value === 'intl');
    }

    openEditAddressModal(target: 'send' | 'return') {
        this.editAddressTarget.set(target);
        const addr = target === 'send' ? this.sendAddress() : this.returnAddress();
        this.tempAddress = { ...addr };
        this.showEditAddressModal.set(true);
    }

    saveEditedAddress() {
        const target = this.editAddressTarget();
        const fullAddress = `${this.tempAddress.fullName}, ${this.tempAddress.address}, ${this.tempAddress.city}, ${this.tempAddress.state} ${this.tempAddress.zipcode}`;
        const updated = { ...this.tempAddress, fullAddress };

        if (target === 'send') {
            this.sendAddress.set(updated);
        } else {
            this.returnAddress.set(updated);
        }
        this.showEditAddressModal.set(false);
    }

    submitShipping() {
        this.shippingAlert.set(null);
        const type = this.shipType();

        if (!type) {
            this.shippingAlert.set('Please select a shipping method.');
            return;
        }

        

        if (type === 'own' || type === 'normal_mail') {
            const addr = this.sendAddress();
           

            const choice: ShippingChoice = {
                type: type,
                userAddress: {
                    fullName: addr.fullName,
                    phone: addr.phone,
                    address: addr.address,
                    city: addr.city,
                    state: addr.state,
                    zipcode: addr.zipcode,
                    email: addr.email
                },
            };
            this.shippingChoice.emit(choice);
            return;
        }

        const isAddressInvalid = (addr: any) => {
            return !addr.fullName || !addr.address || !addr.city || !addr.state || !addr.zipcode;
        };

        // type === 'fedex'
        const choice: ShippingChoice = { type: 'fedex' };

        if (this.openShippingFromOptions()) {
            if (!this.fedexTypeSend()) {
                this.shippingAlert.set('Please select a shipping option for sending your documents.');
                return;
            }
            choice.send = this.fedexTypeSend()!;
            if (choice.send === 'intl') {
                choice.sendIntlAddress = { ...this.sendIntlAddress };
            }
        }

        if (this.openShippingToOptions()) {
            if (!this.fedexTypeReceive()) {
                this.shippingAlert.set('Please select a shipping option for returning your documents.');
                return;
            }
            choice.receive = this.fedexTypeReceive()!;
            if (choice.receive === 'intl') {
                choice.receiveIntlAddress = { ...this.receiveIntlAddress };
            }
        }

        if (!this.openShippingFromOptions() && !this.openShippingToOptions()) {
            this.shippingAlert.set('Please select at least one shipping option (send or receive).');
            return;
        }

        // Attach user address data
        const addr = this.sendAddress();
        
        // If they chose domestic send, ensure address is valid
        if (this.openShippingFromOptions() && choice.send !== 'intl' && isAddressInvalid(addr)) {
            this.shippingAlert.set('Please provide a complete shipping address.');
            this.openEditAddressModal('send');
            return;
        }

        // If they chose domestic receive, ensure return address is valid
        const retAddr = this.returnAddress();
        if (this.openShippingToOptions() && choice.receive !== 'intl' && isAddressInvalid(retAddr)) {
            this.shippingAlert.set('Please provide a complete return address.');
            this.openEditAddressModal('return');
            return;
        }

        choice.userAddress = {
            fullName: addr.fullName,
            phone: addr.phone,
            address: addr.address,
            city: addr.city,
            state: addr.state,
            zipcode: addr.zipcode,
            email: addr.email,
        };

        this.shippingChoice.emit(choice);
    }

    dismiss() {
        this.close.emit();
    }

    private emptyIntlAddress(): IntlAddress {
        return {
            firstName: '', lastName: '', company: '', street: '', address2: '',
            city: '', province: '', country: '', zipcode: '', phone: ''
        };
    }
}
